<template>
    <div class="flex gap-1 items-center" data-testid="chat-message-reaction-bar">
        <BaseTooltip trigger-aria-label="Thumbs up tooltip trigger">
            <template #trigger>
                <BaseButton
                    :theme="thumbUpActive ? 'secondary' : 'tertiary'"
                    icon-only
                    aria-label="Helpful"
                    :aria-pressed="thumbUpActive"
                    :disabled="buttonsDisabled"
                    :class="{ 'opacity-0 group-hover/chat:opacity-100': !isLastMessage && !hasExistingFeedback }"
                    @click="handleThumbUpClick">
                    <ThumbsUp aria-hidden="true" />
                </BaseButton>
            </template>
            <template #content>
                {{ thumbUpActive ? "Remove thumbs up" : "Good response" }}
            </template>
        </BaseTooltip>
        <!-- Feedback issues modal (thumbs down) -->
        <BaseDialog
            v-model:open="showFeedbackIssuesDialog"
            title="Feedback Issues"
            :dialog-content-class="`${dialogContainerClass} ${dialogAnimationClass}`"
            :overlay-class="overlayAnimationClass"
            @update:open="handleCommentPopoverChange">
            <FeedbackIssuesForm :target="feedbackTarget" @save="handleFeedbackFormSave" @dismiss="dismissCommentPopover" />
            <template #trigger>
                <BaseTooltip trigger-aria-label="Thumbs down tooltip trigger">
                    <template #trigger>
                        <BaseButton
                            :theme="thumbDownActive ? 'secondary' : 'tertiary'"
                            icon-only
                            aria-label="Not helpful"
                            :aria-pressed="thumbDownActive"
                            :disabled="buttonsDisabled"
                            :class="{ 'opacity-0 group-hover/chat:opacity-100': !isLastMessage && !hasExistingFeedback }"
                            @click="handleThumbDownClick">
                            <ThumbsDown aria-hidden="true" />
                        </BaseButton>
                    </template>
                    <template #content>
                        {{ thumbDownActive ? "Remove thumbs down" : "Bad response" }}
                    </template>
                </BaseTooltip>
            </template>
        </BaseDialog>

        <BaseTooltip :open="isCopyTooltipOpen" :delay-duration="0" trigger-aria-label="Copy to clipboard tooltip trigger" @update:open="handleCopyTooltipOpen">
            <template #trigger>
                <BaseButton
                    :theme="isCopied ? 'secondary' : 'tertiary'"
                    icon-only
                    aria-label="Copy message"
                    :class="{ 'opacity-0 group-hover/chat:opacity-100': !isLastMessage && !hasExistingFeedback }"
                    @click="handleCopyLine">
                    <Copy aria-hidden="true" />
                </BaseButton>
            </template>
            <template #content>
                <span class="inline-flex items-center gap-1" role="alert">
                    <i v-if="isCopied" class="bi bi-check text-base/none" aria-hidden="true"></i>{{ isCopied ? "Text Copied" : "Copy to clipboard" }}
                </span>
            </template>
        </BaseTooltip>
        <BaseDropdownMenu v-model:open="isMoreMenuOpen" :sections="menuSections" side="top" :side-offset="8" align="start" content-class="min-w-50" @select="handleMoreMenuSelect">
            <template #trigger>
                <BaseButton
                    :theme="isMoreMenuOpen ? 'secondary' : 'tertiary'"
                    icon-only
                    aria-label="More options"
                    :class="{ 'opacity-0 group-hover/chat:opacity-100': !isLastMessage && !hasExistingFeedback }"
                    @click.stop>
                    <i class="bi bi-three-dots-vertical leading-none" aria-hidden="true"></i>
                </BaseButton>
            </template>
        </BaseDropdownMenu>

        <!-- Report issue modal -->
        <BaseDialog
            v-model:open="showReportIssueDialog"
            title="Report Issue"
            :dialog-content-class="dialogAnimationClass"
            :overlay-class="overlayAnimationClass"
            @update:open="handleReportDialogOpen">
            <div :class="dialogContainerClass">
                <div class="flex justify-between items-center mb-4">
                    <h3 class="title-body text-gray-900 font-normal">Would you like to provide further details?</h3>
                    <BaseButton theme="tertiary" icon-only aria-label="Close report issue dialog" @click="hideReportIssueDialog">
                        <X foreground-class="fill-current" />
                    </BaseButton>
                </div>
                <textarea
                    ref="reportIssueTextarea"
                    v-model="reportIssueText"
                    aria-label="report issue feedback input"
                    class="w-full h-32 p-3 body border border-gray-300 rounded-lg resize-none"
                    placeholder="What went wrong?"></textarea>
                <div class="flex justify-end items-center mt-4">
                    <BaseButton theme="primary" size="sm" :disabled="isSubmittingReportIssue" @click="submitReportIssue">
                        {{ isSubmittingReportIssue ? "Submitting..." : "Submit" }}
                    </BaseButton>
                </div>
            </div>
        </BaseDialog>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import { useTimedShow } from "~vue/composables/useTimedShow";
import X from "~vue/icons/X.vue";
import DOMPurify from "dompurify";
import { Marked } from "marked";
import { computed, inject, nextTick, ref, useTemplateRef } from "vue";

import BaseDialog from "./components/BaseDialog.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "./components/BaseDropdownMenu.vue";
import { getFeedbackIssues } from "./Feedback/feedbackIssuesCache";
import FeedbackIssuesForm from "./Feedback/FeedbackIssuesForm.vue";
import Copy from "./icons/Copy.vue";
import HelpBubble from "./icons/HelpBubble.vue";
import ThumbsDown from "./icons/ThumbsDown.vue";
import ThumbsUp from "./icons/ThumbsUp.vue";
import type { ChatMessage } from "./types/chat";
import type { FeedbackTarget, ThumbDirection } from "./types/feedback";
import { logUserInteraction } from "./utils/logUtils";
import { noImageRenderer, sanitizeForChat } from "./utils/markdown";

interface Props {
    message: ChatMessage;
    isLastMessage?: boolean;
    thumbState?: ThumbDirection | null;
    isSubmitting?: boolean;
    hasExistingFeedback?: boolean;
}

interface FeedbackState {
    thumbState: ThumbDirection | null;
    isSubmitting: boolean;
}

interface FeedbackUpdate {
    messageId?: string;
    thumbState: ThumbDirection | null;
    hasComment: boolean;
}

interface FeedbackPayload {
    message_id?: string;
    feedback_kind: string;
    feedback_data: { value: number; issues?: string[] } | null;
    comment?: string | null;
}

const coachingSessionUuid = inject<string | undefined>("coachingSessionUuid");

const props = withDefaults(defineProps<Props>(), {
    isLastMessage: false,
    thumbState: null,
    isSubmitting: false,
    hasExistingFeedback: false,
});

const { message, isLastMessage, hasExistingFeedback } = props;

// Feedback state management
const feedbackState = ref<FeedbackState>({
    thumbState: null,
    isSubmitting: false,
});

const dialogContainerClass = "bg-white rounded-lg shadow-xl md:min-w-162.5 p-6 border border-gray-300";
// Soft enter/exit for the feedback and report-issue dialogs (ENG-1249). reka-ui keeps the
// element mounted (and interactive) through the data-[state=closed] animation, so the exit
// pairs with pointer-events-none — otherwise a fast second click during the 150ms fade lands
// on a still-enabled Submit and re-fires the POST. The `!` is required: reka sets inline
// `pointer-events: auto` on the content while open, which survives into the closed state.
const dialogAnimationClass =
    "max-md:data-[state=open]:animate-sheet-in md:data-[state=open]:animate-dialog-in data-[state=closed]:animate-dialog-out data-[state=closed]:pointer-events-none!";
const overlayAnimationClass = "data-[state=open]:animate-overlay-in data-[state=closed]:animate-dialog-out";

// Simple computed properties based on internal state
const thumbUpActive = computed(() => feedbackState.value.thumbState === "up");
const thumbDownActive = computed(() => feedbackState.value.thumbState === "down");
const buttonsDisabled = computed(() => feedbackState.value.isSubmitting);

// Feedback issues dropdown state
const showFeedbackIssuesDialog = ref(false);
const isSubmittingFeedbackIssues = ref(false);

const feedbackTarget = computed<FeedbackTarget>(() => message.feedback_target ?? "chat_message");

// Report issue dialog state
const showReportIssueDialog = ref(false);
const reportIssueText = ref("");
const isSubmittingReportIssue = ref(false);
const reportIssueTextarea = useTemplateRef("reportIssueTextarea");

// API composables
const thumbFeedbackRequest = useFetch("/api/chat/feedback/turn-feedback/", { immediate: false });

const emit = defineEmits<{
    thoughtProcessClicked: [message: ChatMessage];
    feedbackUpdated: [update: FeedbackUpdate];
}>();

const isMoreMenuOpen = ref(false);
const menuSections: DropdownMenuSection[] = [
    {
        id: "actions",
        items: [
            { id: "report", type: "button", label: "Report an issue", icon: "bi bi-info-circle" },
            { id: "thought-process", type: "button", label: "Explain my thought process", icon: HelpBubble },
        ],
    },
];

const { isShowing: isCopied, show: showCopied, hide: hideCopied } = useTimedShow({ duration: 3000 });
const isCopyTooltipOpen = ref(false);

function handleCopyTooltipOpen(open = false) {
    isCopyTooltipOpen.value = open;
    if (!open) {
        hideCopied();
    }
}

const handleReportClick = () => {
    // Reset on open, not on close: the dialog stays visible through the 150ms exit fade, and
    // resetting there wipes the textarea and re-enables Submit while the user can still see
    // (and for part of it, click) the dialog.
    reportIssueText.value = "";
    isSubmittingReportIssue.value = false;
    showReportIssueDialog.value = true;
    nextTick(() => {
        reportIssueTextarea.value?.focus();
    });
};

const handleMoreMenuSelect = (item: DropdownMenuItem) => {
    if (item.id === "report") {
        handleReportClick();
    } else if (item.id === "thought-process") {
        emit("thoughtProcessClicked", message);
    }
};

// A separator Nadia wrote around content that now renders as its own card reads as a stray rule once copied.
const DANGLING_RULE = /\n+\s*(?:-{3,}|\*{3,}|_{3,})\s*$/;

// Prose can be split across several lines by an interleaved action line, such as an email draft card.
const markdown = computed(() =>
    (message?.lines ?? [])
        .filter((line) => line.type === "md")
        .map((line) => line.content.replace(DANGLING_RULE, ""))
        .join("\n\n"),
);

async function handleCopyLine() {
    const plain = markdown.value;
    if (!plain) {
        return;
    }

    const parser = new Marked({ renderer: noImageRenderer });
    const html = sanitizeForChat(await parser.parse(plain));

    const data = new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([plain], { type: "text/plain" }),
    });
    await navigator.clipboard.write([data]);
    showCopied();
    isCopyTooltipOpen.value = true;

    // Log the copy event with message ID and session ID
    logUserInteraction(
        "message_copied",
        {
            chat_message_id: message.id,
        },
        coachingSessionUuid,
    );
}

// Main feedback handling function
const handleThumbFeedback = async (direction: ThumbDirection, comment: string | null = null, isCommentUpdate = false, issues: string[] = []) => {
    const messageId = message.chat_message_id;
    const currentState = feedbackState.value.thumbState;

    // For comment updates, preserve the existing thumbState
    const newThumbState = isCommentUpdate ? currentState : currentState === direction ? null : direction;

    // Only update optimistic state if not a comment update
    if (!isCommentUpdate) {
        feedbackState.value = {
            thumbState: newThumbState,
            isSubmitting: true,
        };
    }

    // Prepare API payload
    let feedbackData: FeedbackPayload["feedback_data"] = null;
    if (newThumbState) {
        feedbackData = { value: newThumbState === "up" ? 1 : 0 };
        if (issues.length > 0) {
            feedbackData.issues = [...issues];
        }
    }
    const payload: FeedbackPayload = {
        message_id: messageId.toString(),
        feedback_kind: "thumb",
        feedback_data: feedbackData,
    };

    if (newThumbState === null) {
        payload.comment = null;
    } else if (direction === "down" && comment) {
        payload.comment = DOMPurify.sanitize(comment);
    } else if (direction === "up") {
        payload.comment = null;
    }

    try {
        // API call
        await thumbFeedbackRequest.post(payload).execute();

        // Only update state if not a comment update
        if (!isCommentUpdate) {
            feedbackState.value = {
                thumbState: newThumbState,
                isSubmitting: false,
            };
        }

        // Log appropriate interaction based on context
        let eventName;
        if (isCommentUpdate) {
            eventName = "turn_feedback_comment_added";
        } else {
            eventName = newThumbState === null ? "turn_feedback_removed" : "turn_feedback_submitted";
        }

        logUserInteraction(
            eventName,
            {
                chat_message_id: messageId,
                feedback_kind: "thumb",
                feedback_value: newThumbState || direction,
                has_comment: !!comment,
            },
            coachingSessionUuid,
        );

        // Emit feedback update to parent for any external state management
        emit("feedbackUpdated", {
            messageId: messageId.toString(),
            thumbState: newThumbState,
            hasComment: !!comment,
        });
    } catch (error) {
        // Revert optimistic update on error (only if not a comment update)
        if (!isCommentUpdate) {
            feedbackState.value = {
                thumbState: currentState,
                isSubmitting: false,
            };
        }
        throw error; // Re-throw to handle in calling functions
    }
};

function handleThumbUpClick() {
    // Dismiss feedback popover if it's open
    dismissCommentPopover();
    handleThumbFeedback("up");
}

async function handleThumbDownClick() {
    if (thumbDownActive.value) {
        // If thumbs down is already active, remove it
        dismissCommentPopover();
        await handleThumbFeedback("down");
    } else {
        // Warm the issues list while the feedback POST is in flight, so the dialog opens
        // with its content already resolved instead of reflowing mid-enter-animation.
        // Best-effort: a failure here rejects (and evicts the cache); the form retries it
        // on mount and owns the user-facing error state, so this caller swallows it.
        void getFeedbackIssues(feedbackTarget.value).catch(() => {});

        // Send thumbs down feedback immediately
        await handleThumbFeedback("down");

        // Then show floating feedback panel for optional details
        showFeedbackIssuesDialog.value = true;
    }
}

function handleCommentPopoverChange(isOpen: boolean) {
    if (!isOpen) {
        dismissCommentPopover();
    }
}

async function handleFeedbackFormSave(payload: { issues: string[] | null; comments: string | null }) {
    // The dialog stays mounted (and focused) through the 150ms exit; pointer-events-none only
    // blocks the mouse, so Enter/Space on the still-focused Save could re-fire this after close.
    if (!showFeedbackIssuesDialog.value || isSubmittingFeedbackIssues.value || !feedbackState.value.thumbState) {
        return;
    }

    const issues = payload.issues ?? [];
    const comment = (payload.comments ?? "").trim() || null;
    const hasContent = !!comment || issues.length > 0;

    if (hasContent) {
        isSubmittingFeedbackIssues.value = true;
        try {
            await handleThumbFeedback(feedbackState.value.thumbState, comment, true, issues);
        } catch {
            isSubmittingFeedbackIssues.value = false;
            return;
        }
    }

    dismissCommentPopover();
}

function dismissCommentPopover() {
    showFeedbackIssuesDialog.value = false;
    isSubmittingFeedbackIssues.value = false;
}

const handleReportDialogOpen = (isOpen: boolean) => {
    if (!isOpen) {
        hideReportIssueDialog();
    }
};

function hideReportIssueDialog() {
    showReportIssueDialog.value = false;
}

async function submitReportIssue() {
    // Same keyboard-during-exit guard as handleFeedbackFormSave (cancel path: Submit is enabled).
    if (!showReportIssueDialog.value || isSubmittingReportIssue.value) {
        return;
    }

    isSubmittingReportIssue.value = true;
    const messageId = message.chat_message_id;

    const payload = {
        message_id: messageId,
        feedback_kind: "report_issue",
        feedback_data: null,
        comment: reportIssueText.value.trim() ? DOMPurify.sanitize(reportIssueText.value.trim()) : null,
    };

    try {
        await thumbFeedbackRequest.post(payload).execute();

        logUserInteraction(
            "message_report_issue_clicked",
            {
                chat_message_id: messageId,
                comment: reportIssueText.value.trim() || null,
            },
            coachingSessionUuid,
        );

        hideReportIssueDialog();
    } catch {
        isSubmittingReportIssue.value = false;
    }
}

// Initialize feedback state from existing feedback
const initializeFeedbackState = (existingFeedback?: { thumbState?: ThumbDirection | null }) => {
    feedbackState.value = {
        thumbState: existingFeedback?.thumbState || null,
        isSubmitting: false,
    };
};

// Expose method to allow parent to initialize state
defineExpose({
    initializeFeedbackState,
});
</script>
